import React, { useState, useEffect } from 'react';
import { CoachLayout } from '../components/CoachLayout';
import { useAuth } from '../context/AuthContext';
import { authAPI, getErrorMessage } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Euro, Lock, Eye, EyeOff, Plus, X, LogOut, RefreshCw } from 'lucide-react';
import { ProfilePhotoUpload } from '../components/ProfileAvatar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

export default function ProfilePage() {
  const { user, userType, logout, switchToAthlete, switchToCoach, deleteAccount } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [variableRates, setVariableRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingRole, setAddingRole] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Password change states
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const hasDualRole = user?.athlete_account_id || localStorage.getItem('hasDualRole') === 'true';

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setHourlyRate(user.hourly_rate?.toString() || '');
      setVariableRates(user.variable_rates || []);
      setProfilePhoto(user.profile_photo || null);
    }
  }, [user]);

  const handlePhotoUpload = async (file) => {
    setUploadingPhoto(true);
    try {
      const response = await authAPI.uploadPhoto(file);
      setProfilePhoto(response.data.profile_photo);
      toast.success('Foto profilo aggiornata!');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel caricamento della foto'));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {
        name,
        email,
        hourly_rate: parseFloat(hourlyRate) || 0,
        variable_rates: variableRates.map(r => ({
          min_athletes: parseInt(r.min_athletes) || 0,
          max_athletes: parseInt(r.max_athletes) || 0,
          rate_per_hour: parseFloat(r.rate_per_hour) || 0
        }))
      };
      await authAPI.updateProfile(updateData);
      toast.success('Profilo aggiornato con successo!');
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nell\'aggiornamento del profilo'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await authAPI.changePassword({ current_password: currentPassword, new_password: newPassword });
      toast.success('Password modificata con successo!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordSection(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nella modifica della password'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleAddAthleteRole = async () => {
    setAddingRole(true);
    try {
      await authAPI.addAthleteRole();
      localStorage.setItem('hasDualRole', 'true');
      toast.success('Ruolo atleta aggiunto! Ora puoi switchare tra le due viste.');
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nell\'aggiunta del ruolo atleta'));
    } finally {
      setAddingRole(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteAccount();
      toast.success('Account eliminato con successo');
    } catch (error) {
      toast.error('Errore nell\'eliminazione dell\'account');
    }
  };

  const addVariableRate = () => {
    setVariableRates([...variableRates, { min_athletes: '', max_athletes: '', rate_per_hour: '' }]);
  };

  const removeVariableRate = (index) => {
    setVariableRates(variableRates.filter((_, i) => i !== index));
  };

  const updateVariableRate = (index, field, value) => {
    const newRates = [...variableRates];
    newRates[index][field] = value;
    setVariableRates(newRates);
  };

  return (
    <CoachLayout showFab={false}>
      <div data-testid="profile-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-8">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-4xl font-black text-white mb-1 sm:mb-2">Il Mio Profilo</h1>
          <p className="text-zinc-400">Gestisci le tue informazioni personali</p>
        </div>

        {/* Foto Profilo */}
        <div className="glass-card p-6 rounded-xl border border-white/10 mb-4 flex justify-center">
          <ProfilePhotoUpload 
            currentPhoto={profilePhoto}
            name={name}
            onUpload={handlePhotoUpload}
            uploading={uploadingPhoto}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
          {/* Informazioni Personali */}
          <div className="glass-card p-6 rounded-xl border border-white/10">
            <div className="flex items-center space-x-3 mb-6">
              <User className="w-8 h-8 text-[#ccff00]" />
              <h2 className="text-2xl font-bold text-white">Informazioni Personali</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-zinc-300">Nome Completo</Label>
                <Input
                  id="name"
                  data-testid="profile-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] rounded-xl h-12"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-zinc-300">Email</Label>
                <Input
                  id="email"
                  data-testid="profile-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] rounded-xl h-12"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="hourlyRate" className="text-zinc-300">Compenso Orario Fisso (€)</Label>
                <Input
                  id="hourlyRate"
                  data-testid="hourly-rate-input"
                  type="text"
                  inputMode="decimal"
                  value={hourlyRate}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d*\.?\d*$/.test(val)) {
                      setHourlyRate(val);
                    }
                  }}
                  className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] rounded-xl h-12"
                  required
                />
              </div>

              {/* Tariffe Variabili */}
              <div className="space-y-3 pt-4 border-t border-zinc-800">
                <Label className="text-zinc-300">Tariffe variabili in base al numero di atleti in compresenza</Label>
                
                {variableRates.length > 0 && (
                  <div className="space-y-3">
                    {variableRates.map((rate, index) => (
                      <div key={index} className="p-4 bg-zinc-900/50 rounded-xl border border-zinc-800 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-zinc-400 text-sm font-medium">Fascia {index + 1}</span>
                          <button
                            type="button"
                            onClick={() => removeVariableRate(index)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-zinc-400 text-xs">Min Atleti</Label>
                            <Input
                              type="text"
                              inputMode="numeric"
                              value={rate.min_athletes}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || /^\d*$/.test(val)) {
                                  updateVariableRate(index, 'min_athletes', val);
                                }
                              }}
                              placeholder="1"
                              className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] h-10 text-sm rounded-lg"
                            />
                          </div>
                          <div>
                            <Label className="text-zinc-400 text-xs">Max Atleti</Label>
                            <Input
                              type="text"
                              inputMode="numeric"
                              value={rate.max_athletes}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || /^\d*$/.test(val)) {
                                  updateVariableRate(index, 'max_athletes', val);
                                }
                              }}
                              placeholder="5"
                              className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] h-10 text-sm rounded-lg"
                            />
                          </div>
                          <div>
                            <Label className="text-zinc-400 text-xs">€/Ora</Label>
                            <Input
                              type="text"
                              inputMode="decimal"
                              value={rate.rate_per_hour}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                  updateVariableRate(index, 'rate_per_hour', val);
                                }
                              }}
                              placeholder="40"
                              className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] h-10 text-sm rounded-lg"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <Button
                  type="button"
                  variant="outline"
                  onClick={addVariableRate}
                  className="w-full border-[#ccff00]/30 text-[#ccff00] hover:bg-[#ccff00]/10 rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Aggiungi Fascia Tariffa
                </Button>
              </div>

              <Button
                data-testid="save-profile-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold uppercase tracking-wide rounded-xl h-12 shadow-[0_0_15px_rgba(204,255,0,0.3)]"
              >
                {loading ? 'Salvataggio...' : 'Salva Modifiche'}
              </Button>
            </form>
          </div>

          <div className="space-y-3 sm:space-y-4">
            {/* Modifica Password */}
            <div className="glass-card p-6 rounded-xl border border-white/10">
              <div className="flex items-center space-x-3 mb-4">
                <Lock className="w-6 h-6 text-[#ccff00]" />
                <h2 className="text-xl font-bold text-white">Modifica Password</h2>
              </div>

              {!showPasswordSection ? (
                <Button
                  onClick={() => setShowPasswordSection(true)}
                  variant="outline"
                  className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-xl"
                >
                  Cambia Password
                </Button>
              ) : (
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Password Attuale</Label>
                    <div className="relative">
                      <Input
                        type={showCurrentPassword ? "text" : "password"}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] rounded-xl h-12 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                      >
                        {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Nuova Password</Label>
                    <div className="relative">
                      <Input
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] rounded-xl h-12 pr-10"
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                      >
                        {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Conferma Nuova Password</Label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] rounded-xl h-12"
                      required
                      minLength={6}
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowPasswordSection(false);
                        setCurrentPassword('');
                        setNewPassword('');
                        setConfirmPassword('');
                      }}
                      className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-xl"
                    >
                      Annulla
                    </Button>
                    <Button
                      type="submit"
                      disabled={passwordLoading}
                      className="flex-1 bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl"
                    >
                      {passwordLoading ? 'Salvataggio...' : 'Salva'}
                    </Button>
                  </div>
                </form>
              )}
            </div>

            {/* Aggiungi Ruolo Atleta */}
            {!hasDualRole && (
              <div className="glass-card p-6 rounded-xl border border-[#ccff00]/30">
                <div className="flex items-center space-x-3 mb-4">
                  <User className="w-6 h-6 text-[#ccff00]" />
                  <h2 className="text-xl font-bold text-white">Aggiungi Ruolo Atleta</h2>
                </div>

                <p className="text-zinc-400 text-sm mb-4">
                  Vuoi anche visualizzare le tue lezioni come atleta? Aggiungi il ruolo atleta per accedere a entrambe le viste.
                </p>

                <Button
                  data-testid="add-athlete-role-btn"
                  onClick={handleAddAthleteRole}
                  disabled={addingRole}
                  className="w-full bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl shadow-[0_0_15px_rgba(204,255,0,0.3)]"
                >
                  {addingRole ? 'Aggiunta in corso...' : 'Aggiungi Ruolo Atleta'}
                </Button>
              </div>
            )}

            {user?.hourly_rate === 0 && (
              <div className="p-4 bg-yellow-900/20 border border-yellow-700/30 rounded-xl">
                <p className="text-yellow-500 text-sm">
                  ⚠️ Attenzione: Imposta il tuo compenso orario per calcolare correttamente i costi delle lezioni.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Gestione Account */}
        <div className="mt-6 glass-card p-6 rounded-xl border border-white/10">
          <div className="flex items-center space-x-3 mb-4">
            <LogOut className="w-6 h-6 text-[#ccff00]" />
            <h2 className="text-xl font-bold text-white">Gestione Account</h2>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            {hasDualRole && (
              <Button
                onClick={async () => {
                  try {
                    await switchToAthlete();
                  } catch (error) {
                    toast.error('Errore nel cambio profilo');
                  }
                }}
                variant="outline"
                className="flex-1 bg-zinc-800 text-[#ccff00] hover:bg-zinc-700 border border-[#ccff00]/30 rounded-xl h-12"
                data-testid="switch-to-athlete-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Profilo Atleta
              </Button>
            )}
            <Button
              onClick={logout}
              variant="outline"
              className="flex-1 bg-zinc-800 text-white hover:bg-zinc-700 border border-zinc-700 rounded-xl h-12"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </div>

          {/* Elimina Account - piccolo e discreto */}
          <div className="mt-6 pt-4 border-t border-zinc-800">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  data-testid="delete-account-btn"
                  className="text-xs text-zinc-500 hover:text-red-400 transition-colors"
                >
                  Elimina account
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-zinc-900 border border-white/10 rounded-xl">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-white">Sei assolutamente sicuro?</AlertDialogTitle>
                  <AlertDialogDescription className="text-zinc-400">
                    Questa azione non può essere annullata. Questo eliminerà permanentemente il tuo account e rimuoverà tutti i tuoi dati dai nostri server.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-zinc-800 text-white hover:bg-zinc-700 rounded-xl">
                    Annulla
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteAccount}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-xl"
                  >
                    Sì, elimina il mio account
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
    </CoachLayout>
  );
}
